import { useCallback, useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, ExternalLink, Loader2, ShieldCheck, Users } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const AppCard = ({ app, target, active, onPick }) => {
  const pct = Math.min(100, Math.round((app.count / target) * 100));
  return (
    <button data-testid={`tester-app-${app.kind}`} onClick={() => onPick(app)}
      className={`group w-full rounded-2xl border p-5 text-left transition-colors duration-200 ${
        active ? "border-gold bg-ink-800" : "border-ink-600 bg-ink-800/60 hover:border-gold/50"}`}>
      <div className="flex items-start gap-4">
        <img src={app.icon} alt="" className="h-12 w-12 rounded-xl" />
        <div className="min-w-0">
          <p className="font-display text-lg text-slate-100">{app.name}</p>
          <p className="mt-1 text-sm text-slate-400">{app.tagline}</p>
        </div>
      </div>
      <div className="mt-5">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink-600">
          <div className="h-full rounded-full bg-gold transition-all duration-500"
            style={{ width: `${pct}%` }} />
        </div>
        <p data-testid={`tester-count-${app.kind}`}
          className="mt-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
          {app.count} of {target} testers
        </p>
      </div>
    </button>
  );
};

export default function Testers() {
  const [page, setPage] = useState(null);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", device: "" });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/public/testers");
      setPage(data);
      const wanted = new URLSearchParams(window.location.search).get("app");
      setPicked((prev) => prev || data.apps.find((a) => a.kind === wanted) || null);
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/public/testers", { ...form, app_id: picked.app_id });
      setDone(data);
      setForm({ name: "", email: "", device: "" });
      load();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!page) {
    return (
      <div className="grid min-h-screen place-items-center bg-ink-900">
        {error ? <p className="text-sm text-red-400">{error}</p>
               : <Loader2 className="h-5 w-5 animate-spin text-gold" />}
      </div>
    );
  }

  return (
    <div data-testid="testers-page" className="min-h-screen bg-ink-900 px-5 py-14 sm:px-10 lg:px-20">
      <div className="mx-auto max-w-5xl">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
          <ShieldCheck className="h-3.5 w-3.5" /> LimoLinkX · closed testing
        </p>
        <h1 className="mt-6 max-w-3xl font-display text-4xl leading-tight text-slate-50 sm:text-5xl lg:text-6xl">
          Be one of the first to run LimoLinkX on Android.
        </h1>
        <p className="mt-6 max-w-2xl text-base text-slate-400 sm:text-lg">
          Google asks for {page.target} testers to use an app for {page.days} continuous days before
          it can go public. Pick the app that fits you, join the tester group, and keep it installed —
          that's the whole ask.
        </p>

        <div className="mt-14 grid gap-4 md:grid-cols-3">
          {page.apps.map((app) => (
            <AppCard key={app.app_id} app={app} target={page.target}
              active={picked?.app_id === app.app_id}
              onPick={(a) => { setPicked(a); setDone(null); }} />
          ))}
        </div>

        {!picked && (
          <p className="mt-10 flex items-center gap-2 text-sm text-slate-500">
            <ArrowRight className="h-4 w-4 text-gold" /> Choose an app to sign up.
          </p>
        )}

        {picked && !done && (
          <form data-testid="tester-form" onSubmit={submit}
            className="mt-12 max-w-xl rounded-2xl border border-ink-600 bg-ink-800 p-6 sm:p-8">
            <p className="font-display text-xl text-slate-100">Testing {picked.name}</p>
            <p className="mt-2 text-sm text-slate-400">
              Use the Google account that is signed in on the phone you'll test with — Play matches
              testers by that address.
            </p>
            <div className="mt-6 space-y-4">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Name</span>
                <Input data-testid="tester-name" required value={form.name} className="mt-1.5"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alex Moreau" />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Google account email</span>
                <Input data-testid="tester-email" required type="email" value={form.email}
                  className="mt-1.5" placeholder="you@gmail.com"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Phone you'll test on (optional)</span>
                <Input data-testid="tester-device" value={form.device} className="mt-1.5"
                  placeholder="Pixel 8, Android 15"
                  onChange={(e) => setForm({ ...form, device: e.target.value })} />
              </label>
            </div>
            {error && <p data-testid="tester-error" className="mt-4 text-sm text-red-400">{error}</p>}
            <Button data-testid="tester-submit" type="submit" disabled={busy}
              className="mt-6 w-full bg-gold text-ink-900 hover:bg-gold-light sm:w-auto">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Users className="mr-2 h-4 w-4" />}
              Count me in
            </Button>
          </form>
        )}

        {done && (
          <div data-testid="tester-success"
            className="mt-12 max-w-xl rounded-2xl border border-gold/40 bg-gold/5 p-6 sm:p-8">
            <p className="flex items-center gap-2 font-display text-xl text-slate-100">
              <CheckCircle2 className="h-5 w-5 text-gold" />
              {done.already ? "You're already on the list" : "You're on the list"}
            </p>
            <p className="mt-2 text-sm text-slate-300">
              {done.count} of {done.target} testers signed up for {picked.name}. Two short steps left:
            </p>
            <ol className="mt-6 space-y-5">
              <li className="flex gap-4">
                <span className="font-mono text-sm text-gold">01</span>
                <div>
                  <p className="text-sm text-slate-200">
                    Join the tester group with the same Google account.
                  </p>
                  {done.group_url && (
                    <a data-testid="tester-group-link" href={done.group_url} target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 text-sm text-gold hover:underline">
                      {done.group_email} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </li>
              <li className="flex gap-4">
                <span className="font-mono text-sm text-gold">02</span>
                <div>
                  <p className="text-sm text-slate-200">
                    Open the test on Play, install the app, and leave it installed for {done.days} days.
                  </p>
                  {done.opt_in_url ? (
                    <a data-testid="tester-optin-link" href={done.opt_in_url} target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 text-sm text-gold hover:underline">
                      Open the test on Google Play <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <p className="mt-2 text-sm text-slate-500">
                      The Play link lands in your inbox as soon as the test opens.
                    </p>
                  )}
                </div>
              </li>
            </ol>
            <Button data-testid="tester-another" variant="outline" className="mt-8"
              onClick={() => { setDone(null); setPicked(null); }}>
              Test another app
            </Button>
          </div>
        )}

        <p className="mt-16 text-xs text-slate-600">
          We only keep your name, email and device so Play can add you to the test.{" "}
          <a href={page.privacy_url} className="text-slate-500 hover:text-gold">Privacy policy</a>
        </p>
      </div>
    </div>
  );
}
